import styled from '@emotion/styled';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { omit, partition, uniqBy } from 'lodash-es';
import {
  forwardRef,
  type JSX,
  type ReactElement,
  type RefAttributes,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
  useController,
} from 'react-hook-form';

import {
  AutocompleteAsyncFormField,
  AutocompleteAsyncFormFieldRef,
} from '~components/FormFields/AutocompleteAsyncFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { Site } from '~hooks/useSites/models';
import { type DefaultSitesWaypointType, useSites } from '~hooks/useSites/useSites';
import useDefaultSitesQuery from '~queries/useDefaultSitesQuery';
import { Pagination } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

import { AddressOption, AddressType } from './types';

type DefaultSiteMutationPayload = {
  waypointType: DefaultSitesWaypointType;
  siteId: string;
};

// `AddressOption` carries no removal wiring — SiteSelection's
// transformAddressOption attaches the per-source DELETE closure and stamps the
// reactive pending flag that drives the inline spinner/disabled ×. Options that
// exist only as optimistic temp additions get a local, network-free removal
// instead (see `withLocalTempRemoval`), hence the sync-or-async signature.
type DeletableAddressOption = AddressOption & {
  isRemovalPending?: boolean;
  onDelete?: () => void | Promise<void>;
};

interface LocationSelectorProps<TFieldValues extends FieldValues = FieldValues> {
  addressLabel: string;
  addressName: string;
  allowSuggestions?: boolean;
  companyId?: string | null;
  control: Control<TFieldValues>;
  disabled?: boolean;
  errors: FieldErrors<TFieldValues>;
  hideAddDefaultSiteAction?: boolean;
  isRequired?: boolean;
  latLngLabel: string;
  latLngName: string;
  onDefaultSiteAdded?: (payload: DefaultSiteMutationPayload) => void;
  onlyDefaultSites?: boolean;
  onTempDefaultSiteRemoved?: (payload: DefaultSiteMutationPayload) => void;
  orderId?: Nullable<string>;
  prefilledValue?: string;
  projectId?: Nullable<string>;
  renderCustomLabel?: ({ isRequired }: { isRequired?: boolean }) => Nullable<JSX.Element>;
  transformAddressOption?: (addressOption: AddressOption) => DeletableAddressOption;
  waypointType?: DefaultSitesWaypointType;
}

const AddSiteToDefaultListButton = styled(Button)`
  /*
    Needed to align with other fields as there's a 4px bottom margin on them.
    Need to remove the bottom margins eventually
  */
  margin-bottom: 4px;
`;

const PlusIcon = styled(Typography)`
  font-size: 24px;
`;

// getSiteById can stall indefinitely behind interception proxies (TRE-16178 —
// the affected org sits behind Zscaler), leaving a selection that looks
// applied but never reaches the form. After 10s — far beyond healthy API
// latency — the selection degrades to the raw dropdown option instead: its
// site_id is all the create payload needs, and the backend hydrates the rest.
const SITE_HYDRATION_TIMEOUT_MS = 10000;

const constructTypeaheadDropdownItem = (site: Site, type: AddressType): AddressOption =>
  ({
    type,
    lat: site.lat,
    lng: site.lon,
    siteId: site.id,
    id: site.id,
    name: site.name,
    externalId: site.externalId,
    geofence: site.nextBillionGeofence,
    companyShare: site.companyShare,
  }) as AddressOption;

const getRemoveDefaultSiteLabel = (sources: AddressOption['sources']) => {
  const isProjectDefault = Boolean(sources?.some((source) => source.type === 'Project'));
  const isOrderDefault = Boolean(sources?.some((source) => source.type === 'Order'));

  if (isProjectDefault && isOrderDefault) {
    return t('actions.remove_site_from_default_project_and_order_list');
  }

  if (isOrderDefault) {
    return t('actions.remove_site_from_default_order_list');
  }

  return t('actions.remove_site_from_default_project_list');
};

interface InlineDefaultSitesProps {
  addressName: string;
  disabled: boolean;
  isFetching: boolean;
  onSelect: (rawOption: AddressOption) => void;
  selectedSiteId: Nullable<string>;
  sites: {
    deletable: DeletableAddressOption;
    isTemporary: boolean;
    raw: AddressOption;
  }[];
}

// Inline callout surfacing the waypoint's default sites below the selector:
// each name is a one-click select, and — where removal is wired — a sibling ×
// re-uses the dropdown's per-source delete.
const InlineDefaultSites = ({
  addressName,
  disabled,
  isFetching,
  onSelect,
  selectedSiteId,
  sites,
}: InlineDefaultSitesProps) => {
  if (!sites.length && !isFetching) {
    return null;
  }

  if (!sites.length) {
    return (
      // One text-line placeholder sized to the callout's 12px row; half-width
      // reads as "a short list is coming" without shifting the layout. Margins
      // match the loaded callout so the row doesn't jump when data arrives.
      <Skeleton
        data-test-id={`inline-default-sites-skeleton-${addressName}`}
        variant="text"
        sx={{ fontSize: '12px', mb: 1, mt: 1, width: '50%' }}
      />
    );
  }

  return (
    <Box
      data-test-id={`inline-default-sites-${addressName}`}
      sx={{
        alignItems: 'center',
        columnGap: 1,
        display: 'flex',
        flexWrap: 'wrap',
        // Breathing room between the selector above and the next form row
        // below (Drop Off / Staging labels) without pushing the rows apart
        // when no defaults exist (the callout is absent entirely then).
        mb: 1,
        mt: 1,
        rowGap: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontSize: '12px', fontWeight: 600 }}
      >
        {`${t('common.default_sites')}:`}
      </Typography>

      {isFetching && <CircularProgress size={12} />}

      {sites.map(({ raw, deletable, isTemporary }) => {
        const isSelected =
          Boolean(selectedSiteId) && (raw.siteId ?? raw.id) === selectedSiteId;
        // A temp addition has no server record yet, so its × undoes a local
        // pending change rather than deleting a persisted default — say so, and
        // keep a distinct test id so the two variants are told apart.
        const removeLabel = isTemporary
          ? t('actions.remove_site_from_new_entity_default_list')
          : getRemoveDefaultSiteLabel(deletable.sources);
        const removeTestId = isTemporary
          ? `inline-default-site-remove-temp-${addressName}-${raw.id}`
          : `inline-default-site-remove-${addressName}-${raw.id}`;

        return (
          // Chip treatment: fully-rounded pill with a subtle mode-aware fill
          // (`action.hover` / `divider` track light & dark automatically);
          // the selected chip keeps the prototype's treadYellow @ 15% tint.
          <Box
            key={raw.id}
            sx={(theme) => ({
              alignItems: 'center',
              backgroundColor: isSelected
                ? alpha(theme.brandV2.colors.treadYellow, 0.15)
                : theme.palette.action.hover,
              border: `1px solid ${
                isSelected
                  ? alpha(theme.brandV2.colors.treadYellow, 0.4)
                  : theme.palette.divider
              }`,
              borderRadius: '999px',
              display: 'flex',
              gap: 0.25,
              px: 1,
              py: 0.25,
            })}
          >
            {disabled ? (
              <Typography sx={{ fontSize: '12px' }}>{raw.name}</Typography>
            ) : (
              <>
                <Typography
                  component="button"
                  type="button"
                  data-test-id={`inline-default-site-${addressName}-${raw.id}`}
                  onClick={() => onSelect(raw)}
                  sx={(theme) => ({
                    background: 'transparent',
                    border: 'none',
                    color: theme.semantic.linkText,
                    cursor: 'pointer',
                    fontSize: '12px',
                    p: 0,
                  })}
                >
                  {raw.name}
                </Typography>

                {deletable.onDelete && (
                  <Tooltip title={removeLabel}>
                    <span>
                      <IconButton
                        aria-label={removeLabel}
                        data-test-id={removeTestId}
                        disabled={deletable.isRemovalPending}
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={(event) => {
                          // The × must never select the site: stop the click
                          // from reaching any surrounding handlers.
                          event.stopPropagation();
                          deletable.onDelete?.();
                        }}
                      >
                        {deletable.isRemovalPending ? (
                          <CircularProgress size={10} />
                        ) : (
                          <Close sx={{ fontSize: '12px' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export type SiteSelectorWithDefaultSitesHandler = {
  onDefaultSiteRemoved: (addressOption: AddressOption) => void;
  resetSuggestions: () => void;
};

const SiteSelectorWithDefaultSitesInner = <TFieldValues extends FieldValues>(
  props: LocationSelectorProps<TFieldValues>,
  ref: React.ForwardedRef<SiteSelectorWithDefaultSitesHandler>,
) => {
  const {
    companyId,
    addressLabel,
    addressName,
    latLngLabel,
    latLngName,
    control,
    disabled = false,
    errors,
    renderCustomLabel,
    isRequired,
    allowSuggestions = true,
    projectId,
    orderId,
    prefilledValue,
    waypointType,
    onDefaultSiteAdded,
    onTempDefaultSiteRemoved,
    hideAddDefaultSiteAction = false,
    transformAddressOption,
    onlyDefaultSites = false,
  } = props;
  const autocompleteRef = useRef<AutocompleteAsyncFormFieldRef>(null);
  const queryClient = useQueryClient();
  const { toasterStore } = useStores();
  const { field } = useController<TFieldValues>({
    control,
    name: addressName as FieldPath<TFieldValues>,
  });
  const [tempNewDefaultSites, setTempNewDefaultSitesState] = useState<AddressOption[]>(
    [],
  );
  const [selectedSiteId, setSelectedSiteId] = useState<Nullable<string>>(
    field?.value?.siteId || null,
  );
  const [isSavingDefaultSite, setIsSavingDefaultSite] = useState(false);

  const addressProvider: SupportedAddressProvider =
    SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH;
  const { getPlaces, getAddressItemByGoogleMapsPlaceId } = useGeocode({
    addressProvider,
  });

  const {
    addSiteToOrderDefaultSites,
    addSiteToProjectDefaultSites,
    getCompanySitesTypeahead,
    getSiteById,
  } = useSites();

  // One merged request per entity returns both pickup + drop_off (already
  // deduped + hydrated server-side). The pickup and drop_off selector instances
  // share the query key, so React Query collapses them into a single network call.
  const { defaultSites, isFetching } = useDefaultSitesQuery({
    companyId,
    orderId,
    projectId,
  });
  const defaultSitesForWaypoint = useMemo(
    () => (waypointType ? defaultSites[waypointType] : []),
    [defaultSites, waypointType],
  );

  // Optimistic additions the server doesn't know about yet. The merge always
  // puts server rows first, so anything still listed here has no persisted
  // record — and therefore no `sources` to drive a targeted DELETE.
  const temporaryDefaultSiteIds = useMemo(() => {
    const persistedSiteIds = new Set(defaultSitesForWaypoint.map(({ id }) => id));

    return new Set(
      tempNewDefaultSites
        .map(({ id }) => id)
        .filter((siteId) => !persistedSiteIds.has(siteId)),
    );
  }, [defaultSitesForWaypoint, tempNewDefaultSites]);

  // Demote a removed default site's dropdown row back to a plain company site:
  // it keeps its place in the suggestions but leaves the "Default Sites" group
  // and loses its delete affordance. Shared by the persisted-removal imperative
  // handle and the local temp-site removal below.
  const demoteDefaultSiteInSuggestions = useCallback((siteId: Nullable<string>) => {
    if (!autocompleteRef.current) {
      return;
    }

    const currentSitesList = autocompleteRef.current.getSuggestions();
    const updatedSitesList = [...currentSitesList].map((site) => {
      if (site.siteId === siteId) {
        site.type = AddressType.Sites;
        site.onDelete = undefined;
      }

      return site;
    });

    const [defaultSites, sites] = partition(
      updatedSitesList,
      (site) => site.type === AddressType.DefaultSites,
    );

    autocompleteRef.current.updateSuggestions([...defaultSites, ...sites]);
  }, []);

  // Undo an optimistic addition: drop it from local state, demote its dropdown
  // row, and let the parent form drop the matching pending post-create POST.
  // No network call — there is no server record to delete yet.
  const removeTempDefaultSite = useCallback(
    (siteId: string) => {
      setTempNewDefaultSitesState((sites) =>
        sites.filter((site) => (site.siteId ?? site.id) !== siteId),
      );

      demoteDefaultSiteInSuggestions(siteId);

      if (waypointType) {
        onTempDefaultSiteRemoved?.({ siteId, waypointType });
      }
    },
    [demoteDefaultSiteInSuggestions, onTempDefaultSiteRemoved, waypointType],
  );

  const withLocalTempRemoval = useCallback(
    (addressOption: AddressOption): DeletableAddressOption => ({
      ...addressOption,
      onDelete: () => removeTempDefaultSite(addressOption.siteId ?? addressOption.id),
    }),
    [removeTempDefaultSite],
  );

  // Mirrors the dropdown's merge (`fetchDefaultSitesList`): server rows first so
  // they win dedup over optimistic temp additions, transformed so real default
  // sites (with `sources`) carry their onDelete and temp additions carry the
  // local removal. Selection always writes the RAW option — neither closure may
  // land in RHF form state.
  const inlineDefaultSites = useMemo(
    () =>
      uniqBy([...defaultSitesForWaypoint, ...tempNewDefaultSites], 'id')
        .map((rawOption) => {
          const isTemporary = temporaryDefaultSiteIds.has(rawOption.id);
          const deletableOption: DeletableAddressOption = isTemporary
            ? withLocalTempRemoval(rawOption)
            : (transformAddressOption?.(rawOption) ?? rawOption);

          return { deletable: deletableOption, isTemporary, raw: rawOption };
        })
        .filter(({ deletable }) => !deletable.discardedAt),
    [
      defaultSitesForWaypoint,
      tempNewDefaultSites,
      temporaryDefaultSiteIds,
      transformAddressOption,
      withLocalTempRemoval,
    ],
  );

  const handleInlineDefaultSiteSelect = (rawOption: AddressOption) => {
    // Merged-query default-site options are already fully hydrated (parsed via
    // `Site.parseNested` in useDefaultSitesQuery), so the option is written to
    // the form directly — no `getSiteById` re-hydration, selection stays
    // synchronous. SiteSelection's watch effect handles lat/lng and markers.
    field.onChange(rawOption);
    setSelectedSiteId(rawOption.siteId ?? rawOption.id);
  };

  useImperativeHandle(
    ref,
    () => ({
      onDefaultSiteRemoved: (addressOption: AddressOption) => {
        setTempNewDefaultSitesState((sites) => {
          return sites.filter((site) => site.siteId !== addressOption.siteId);
        });

        demoteDefaultSiteInSuggestions(addressOption.siteId);
      },
      resetSuggestions: () => {
        // `reset()` (NOT `updateSuggestions([])`) both clears the suggestions
        // and restores `allowFetch`, so the next open refetches server truth
        // and rebuilds correct single-source onDelete closures — used after a
        // partial removal failure, where pruning would hide a still-valid source.
        autocompleteRef.current?.reset();
      },
    }),
    [demoteDefaultSiteInSuggestions],
  );

  const handleSelect = async (site: AddressOption) => {
    if ([AddressType.Sites, AddressType.DefaultSites].includes(site.type)) {
      let siteDetails: Site;
      let hydrationTimeoutId: number | undefined;

      try {
        siteDetails = await Promise.race([
          getSiteById(companyId ?? '', site.id),
          new Promise<never>((_, reject) => {
            hydrationTimeoutId = window.setTimeout(() => {
              reject(new Error(`Timed out fetching details for site ${site.id}`));
            }, SITE_HYDRATION_TIMEOUT_MS);
          }),
        ]);
      } catch (error) {
        if (!isAxiosError(error)) {
          Sentry.captureException(error);
        }

        setSelectedSiteId(site.siteId ?? site.id);

        return omit(site as DeletableAddressOption, ['isRemovalPending', 'onDelete']);
      } finally {
        window.clearTimeout(hydrationTimeoutId);
      }

      setSelectedSiteId(siteDetails.id);

      const address = {
        type: site.type,
        lat: siteDetails.lat,
        lng: siteDetails.lng,
        siteId: siteDetails.id,
        id: siteDetails.id,
        name: siteDetails.name,
        geofence: siteDetails.nextBillionGeofence,
        externalId: siteDetails.externalId,
        companyShare: siteDetails.companyShare,
      } as AddressOption;

      return address;
    }

    if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
      const addressItem = await getAddressItemByGoogleMapsPlaceId(
        String(site.placeId),
        site.address?.fullAddress ?? undefined,
      );

      const addressOption: AddressOption = {
        lat: addressItem.lat,
        lng: addressItem.lng,
        placeId: addressItem.placeId,
        id: addressItem.id,
        name: addressItem.nameWithAddress,
        siteId: null,
        type: AddressType.Suggestions,
        address: addressItem,
        discardedAt: null,
      };

      return addressOption;
    }

    return site;
  };
  // When user first clicks into the field, fetch default sites
  // If a cursor is present in the call, we are fetching more paginated
  // Results. So only add on additional sites to the list for those
  // Sites that have paginated results
  const fetchDefaultSitesList = useCallback(
    async ({ cursor, query }: { cursor?: string; query?: string }) => {
      let defaultSites: { data: AddressOption[]; pagination: Nullable<Pagination> } = {
        data: [],
        pagination: null,
      };

      // Don't re-render default sites if we are scrolling
      // As these don't have paginated results and we want to
      // Prevent duplicating these sites in the dropdown
      if (!cursor) {
        // The merged query already returns project + order default sites for this
        // waypoint, deduped and hydrated server-side (each carrying its `sources`).
        // Server rows go first so they win dedup over optimistic temp additions,
        // which lack `sources` and therefore can't drive a targeted DELETE.
        defaultSites.data = uniqBy(
          [...defaultSitesForWaypoint, ...tempNewDefaultSites],
          'id',
        );
      }
      const shouldFetchCompanySites = onlyDefaultSites
        ? !!autocompleteRef.current?.getInputValue() || !!query
        : true;
      const queryText =
        query !== ''
          ? query
          : (field.value?.name ?? autocompleteRef.current?.getInputValue());

      if (companyId) {
        const { data: sites = [], pagination } = await getCompanySitesTypeahead({
          companyId,
          cursor,
          query: !onlyDefaultSites ? undefined : queryText,
          onlyActiveSites: true,
        });
        const allSites = shouldFetchCompanySites ? sites : [];
        defaultSites = {
          pagination,
          data: [
            ...uniqBy(
              [
                ...defaultSites.data,
                ...allSites.map((site) =>
                  constructTypeaheadDropdownItem(site, AddressType.Sites),
                ),
              ],
              'id',
            ),
          ],
        };
      }

      return defaultSites;
    },
    [
      companyId,
      constructTypeaheadDropdownItem,
      defaultSitesForWaypoint,
      getCompanySitesTypeahead,
      tempNewDefaultSites,
      onlyDefaultSites,
    ],
  );

  // Filters sites list based on query
  const fetchTypeaheadSitesList = async ({
    query,
    cursor,
  }: {
    query: string;
    cursor?: string;
  }) => {
    let filteredSites: { data: AddressOption[]; pagination: Nullable<Pagination> } = {
      data: [],
      pagination: null,
    };

    // Don't re-render default sites if we are scrolling as
    // These don't have paginated results
    if (!cursor) {
      filteredSites.data = uniqBy(
        [...defaultSitesForWaypoint, ...tempNewDefaultSites],
        'id',
      ).filter(({ name }) => name.includes(query));
    }

    if (companyId) {
      const { data: sites = [], pagination } = await getCompanySitesTypeahead({
        companyId,
        query,
        cursor,
        onlyActiveSites: true,
      });

      filteredSites = {
        pagination,
        data: [
          ...filteredSites.data,
          ...sites.map(
            (site) =>
              ({
                type: AddressType.Sites,
                lat: site.lat,
                lng: site.lon,
                siteId: site.id,
                id: site.id,
                name: site.name,
                externalId: site.externalId,
                geofence: site.nextBillionGeofence,
                companyShare: site.companyShare,
              }) as AddressOption,
          ),
        ],
      };
    }

    // Suggested addresses
    const places = (await getPlaces({ query })) || [];

    if (places) {
      filteredSites = {
        ...filteredSites,
        data: [
          ...filteredSites.data,
          ...places.slice(0, 10).map((place) => ({
            lat: place.lat,
            lng: place.lng,
            placeId: place.placeId,
            id: place.id,
            name: place.nameWithAddress,
            siteId: null,
            address: place,
            type: AddressType.Suggestions,
            discardedAt: null,
          })),
        ],
      };
    }

    return filteredSites;
  };

  // Update sites dropdown with temp default sites list
  const updateSitesListWithTempDefaultSites = useCallback(
    (newDefaultSitesList: AddressOption[]) => {
      if (autocompleteRef.current) {
        const currentSitesList = autocompleteRef.current.getSuggestions();
        const updatedSitesList = [
          // Temp rows have no server record, so they carry the local removal
          // rather than the per-source DELETE closure.
          ...newDefaultSitesList.map(withLocalTempRemoval),
          ...currentSitesList,
        ];

        autocompleteRef.current.updateSuggestions(updatedSitesList);
        autocompleteRef.current.refresh();
      }
    },
    [autocompleteRef.current, withLocalTempRemoval],
  );

  // Overall handler to fetch sites list for initial load and with
  // Typeahead
  const fetchSitesByCompanyId = async (
    additionalProps: { query?: string; cursor?: string } = {},
  ) => {
    if (additionalProps?.query && allowSuggestions) {
      return await fetchTypeaheadSitesList({ query: additionalProps.query });
    }

    return await fetchDefaultSitesList({
      cursor: additionalProps.cursor,
      query: additionalProps.query,
    }).then((response) => {
      return {
        ...response,
        data: response.data.map((addressOption) => {
          // Parity with the inline callout: temp additions get the local
          // removal, persisted rows get the per-source DELETE closure.
          if (temporaryDefaultSiteIds.has(addressOption.id)) {
            return withLocalTempRemoval(addressOption);
          }

          return transformAddressOption?.(addressOption) ?? addressOption;
        }),
      };
    });
  };

  const onSuccess = useCallback(
    (resetAutocomplete = true) => {
      if (resetAutocomplete) {
        autocompleteRef.current?.reset();
      }

      autocompleteRef.current?.resetCursor();
    },
    [autocompleteRef.current],
  );

  const onError = useCallback(() => {
    toasterStore.push(
      alert(
        t('errors.issue_adding_site_to_project_default_sites_list'),
        AlertTypes.error,
      ),
    );
  }, [alert, AlertTypes.error, toasterStore]);

  const handleAddSiteToDefaultList = useCallback(async () => {
    if (selectedSiteId && waypointType) {
      setIsSavingDefaultSite(true);
      try {
        if (projectId) {
          try {
            const newlyAddedSite = await addSiteToProjectDefaultSites({
              projectId,
              siteId: selectedSiteId,
              waypointType,
              companyId: companyId ?? undefined,
            });
            if (newlyAddedSite.id) {
              await queryClient.invalidateQueries({ queryKey: ['defaultSites'] });
            }
            onSuccess(true);
          } catch {
            onError();
          }
        }

        if (orderId) {
          try {
            const newlyAddedSite = await addSiteToOrderDefaultSites({
              orderId,
              siteId: selectedSiteId,
              waypointType,
              companyId: companyId ?? undefined,
            });
            if (newlyAddedSite.id) {
              await queryClient.invalidateQueries({ queryKey: ['defaultSites'] });
            }
            onSuccess(true);
          } catch {
            onError();
          }
        }

        if (projectId && orderId) {
          toasterStore.push(
            alert(
              t('approvals.site_added_to_default_project_and_order_list'),
              AlertTypes.success,
            ),
          );
        } else if (projectId) {
          toasterStore.push(
            alert(t('approvals.site_added_to_default_project_list'), AlertTypes.success),
          );
        } else if (orderId) {
          toasterStore.push(
            alert(t('approvals.site_added_to_default_order_list'), AlertTypes.success),
          );
        }

        // For creating a new order, fake the addition of a tempNewDefaultSites
        // Site being added to the default list
        // By adding the site to the a new state call
        if (!orderId) {
          // Fetch the site details and convert
          // To AddressOption
          const newDefaultSite = await getSiteById(companyId ?? '', selectedSiteId);
          // Transform site to AddressOption
          const transformSiteToAddressOption = constructTypeaheadDropdownItem(
            newDefaultSite,
            AddressType.DefaultSites,
          );

          // Add new site to Default Sites List
          setTempNewDefaultSitesState((tempNewDefaultSites) =>
            uniqBy([...tempNewDefaultSites, transformSiteToAddressOption], 'id'),
          );

          onDefaultSiteAdded?.({ siteId: selectedSiteId, waypointType });

          onSuccess(false);
          updateSitesListWithTempDefaultSites(tempNewDefaultSites);
        }
      } finally {
        setIsSavingDefaultSite(false);
      }
    }
  }, [
    selectedSiteId,
    orderId,
    projectId,
    waypointType,
    addSiteToOrderDefaultSites,
    addSiteToProjectDefaultSites,
    AlertTypes,
    alert,
    companyId,
    getSiteById,
    onDefaultSiteAdded,
    onError,
    onSuccess,
    queryClient,
    tempNewDefaultSites,
    toasterStore,
    updateSitesListWithTempDefaultSites,
  ]);

  return (
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ width: '1200px' }}>
          {!addressLabel && renderCustomLabel?.({ isRequired })}

          <AutocompleteAsyncFormField
            ref={autocompleteRef}
            control={control}
            errors={errors}
            name={addressName}
            getValue={(item) => item.id}
            getLabel={(item) => {
              const isDiscarded = Boolean(item?.discardedAt);

              let label = item.name || '';

              // Add external_id if it exists
              if (item.externalId) {
                label = `${label} - ${item.externalId}`;
              }

              if (isDiscarded) {
                return `${label} [${t('common.deleted').toUpperCase()}]`;
              }

              return label;
            }}
            label={addressLabel}
            onSelect={handleSelect}
            isRequired={isRequired}
            asyncCallback={fetchSitesByCompanyId}
            prefilledValue={prefilledValue}
            groupBy={(option) => option.type}
            // StartSearchPosition is used by getPlaces hook to center NB search on its lat/lon values
            // ExtraRequestOptions={{ startSearchPosition }}
            clearable={!disabled}
            disabled={disabled}
            inputProps={{
              style: {
                fontSize: '12px',
              },
            }}
            debounceTime={500}
            listboxWidth={500}
          />
        </Box>

        <TextFormField
          control={control}
          errors={errors}
          name={latLngName}
          type="text"
          disabled={true}
          placeholder={`${t('form_fields.lat_lon_placeholder')}`}
          label={latLngLabel}
          InputProps={{ sx: { fontSize: '12px' } }}
        />

        {!hideAddDefaultSiteAction ? (
          <Tooltip title={`${t('actions.add_site_to_default_list')}`}>
            <span>
              <AddSiteToDefaultListButton
                onClick={handleAddSiteToDefaultList}
                disabled={!selectedSiteId}
                variant="contained"
                loading={isSavingDefaultSite}
                sx={{ mt: '17px' }}
              >
                <PlusIcon align="center">+</PlusIcon>
              </AddSiteToDefaultListButton>
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ minWidth: 64, flexShrink: 0, mt: '17px', mb: '4px' }} />
        )}
      </Box>

      {/* Rendered outside the address column so the wrapping chip row can use
          the full row width (selector + coordinates + add button). */}
      {waypointType && (
        <InlineDefaultSites
          addressName={addressName}
          disabled={disabled}
          isFetching={isFetching}
          onSelect={handleInlineDefaultSiteSelect}
          selectedSiteId={field.value?.siteId ?? field.value?.id ?? null}
          sites={inlineDefaultSites}
        />
      )}
    </Box>
  );
};

export const SiteSelectorWithDefaultSites = forwardRef(
  SiteSelectorWithDefaultSitesInner,
) as <TFieldValues extends FieldValues = FieldValues>(
  props: LocationSelectorProps<TFieldValues> &
    RefAttributes<SiteSelectorWithDefaultSitesHandler>,
) => ReactElement;
